/* ─────────────────────────────────────────────────────────────────────────
   The board — the real XEM8305 assembly, converted from its STEP file.

   The glTF arrives as ~430 named parts (U1, C16:4, R1:88 …). Drawing them as
   430 objects would be 430+ draw calls, so on load they are baked into
   "waves": parts sorted outward from the FPGA and merged per material, a
   handful of waves at a time. Each wave then drops onto its footprint as the
   Build chapter scrubs — the same order the blueprint drew them in.

   Everything is in "board space": origin at the centre of the board's top
   face, +Y up, 1 unit = 10 cm (so the board is 0.83 × 0.53).
   ───────────────────────────────────────────────────────────────────────── */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { clamp, easeOut, invLerp } from './timeline';

const UNIT = 10; // metres → stage units

/* ── blueprint line shader: lines grow from their start vertex as uReveal passes ── */
export function makeLineMaterial(color = 0x0a0a14, accent = 0xff0004) {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uReveal: { value: 0 },
      uOpacity: { value: 1 },
      uColor: { value: new THREE.Color(color) },
      uAccent: { value: new THREE.Color(accent) },
      uHot: { value: 1 },
    },
    vertexShader: /* glsl */`
      attribute float aOrder;
      attribute float aKind;
      varying float vOrder;
      varying float vKind;
      void main() {
        vOrder = aOrder; vKind = aKind;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */`
      uniform float uReveal, uOpacity, uHot;
      uniform vec3 uColor, uAccent;
      varying float vOrder;
      varying float vKind;
      void main() {
        if (vOrder > uReveal) discard;
        float tip = 1.0 - smoothstep(0.0, 0.035, uReveal - vOrder);
        vec3 c = mix(uColor, uAccent, max(tip * uHot, step(0.5, vKind)));
        float a = uOpacity * mix(0.55, 0.95, step(0.5, vKind));
        gl_FragColor = vec4(c, a);
      }`,
  });
}

/* A tiny builder for LineSegments carrying per-vertex draw order. */
export class LineBuilder {
  constructor() { this.p = []; this.o = []; this.k = []; }
  seg(a, b, o0, o1, kind = 0) {
    this.p.push(a.x, a.y, a.z, b.x, b.y, b.z);
    this.o.push(o0, o1); this.k.push(kind, kind);
  }
  /* polyline whose draw order runs o0 → o1 proportional to length */
  poly(pts, o0, o1, kind = 0, closed = false) {
    const P = closed ? [...pts, pts[0]] : pts;
    let L = 0; for (let i = 1; i < P.length; i++) L += P[i].distanceTo(P[i - 1]);
    let acc = 0;
    for (let i = 1; i < P.length; i++) {
      const d = P[i].distanceTo(P[i - 1]);
      const a = o0 + (o1 - o0) * (acc / L), b = o0 + (o1 - o0) * ((acc + d) / L);
      this.seg(P[i - 1], P[i], a, b, kind); acc += d;
    }
  }
  build(material) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.p, 3));
    g.setAttribute('aOrder', new THREE.Float32BufferAttribute(this.o, 1));
    g.setAttribute('aKind', new THREE.Float32BufferAttribute(this.k, 1));
    const l = new THREE.LineSegments(g, material);
    l.frustumCulled = false;
    return l;
  }
}

export function roundedRect(x0, z0, x1, z1, r, y = 0, seg = 6) {
  const pts = [];
  const corners = [[x1 - r, z0 + r, -Math.PI / 2], [x1 - r, z1 - r, 0], [x0 + r, z1 - r, Math.PI / 2], [x0 + r, z0 + r, Math.PI]];
  for (const [cx, cz, a0] of corners)
    for (let i = 0; i <= seg; i++) { const a = a0 + (i / seg) * (Math.PI / 2); pts.push(new THREE.Vector3(cx + Math.cos(a) * r, y, cz + Math.sin(a) * r)); }
  return pts;
}

/* Quantized glTF attributes can't be transformed in place — copy to float. */
function toFloatGeometry(src, matrix) {
  const g = new THREE.BufferGeometry();
  for (const name of ['position', 'normal']) {
    const a = src.attributes[name]; if (!a) continue;
    const arr = new Float32Array(a.count * 3);
    for (let i = 0; i < a.count; i++) { arr[i * 3] = a.getX(i); arr[i * 3 + 1] = a.getY(i); arr[i * 3 + 2] = a.getZ(i); }
    g.setAttribute(name, new THREE.BufferAttribute(arr, 3));
  }
  if (src.index) g.setIndex(Array.from(src.index.array));
  g.applyMatrix4(matrix);
  if (!g.attributes.normal) g.computeVertexNormals();
  return g;
}

/* Re-grade the CAD colours into materials that read as real hardware. */
function gradeMaterial(color) {
  const c = color.clone();
  const hsl = {}; c.getHSL(hsl);
  const m = { color: c, roughness: 0.55, metalness: 0.05 };
  if (hsl.s > 0.5 && hsl.h > 0.08 && hsl.h < 0.17 && hsl.l > 0.35) { m.metalness = 1; m.roughness = 0.28; }            // gold / pads
  else if (hsl.s < 0.08 && hsl.l > 0.55 && hsl.l < 0.85) { m.metalness = 0.85; m.roughness = 0.32; }                  // shields / pins
  else if (hsl.l < 0.06) { m.color = new THREE.Color(0x0e0f12); m.roughness = 0.42; m.metalness = 0.1; }            // mask / packages
  else if (hsl.s < 0.05 && hsl.l >= 0.9) { m.roughness = 0.35; }                                                      // white parts
  else if (hsl.h < 0.06 && hsl.s > 0.6) { m.roughness = 0.45; }                                                       // copper-orange caps
  const mat = new THREE.MeshStandardMaterial(m);
  mat.envMapIntensity = 0.9;
  return mat;
}

/* The CAD board is a black solder mask over an olive core. Real boards read
   green, so the mask is re-dyed here; pads, silkscreen and parts are untouched.
   The mask face starts out as bare FR-4 and only turns green where the mask
   front (uMaskX, board-local x) has flowed over it. */
const FR4 = 0xb3a56b, MASK = 0x0f4f2a, EDGE = 0xb8aa72;
function boardMaterial(color, layers) {
  const hsl = {}; color.getHSL(hsl);
  const mask = hsl.l < 0.12;
  // the mask is a glossy lacquer: a clearcoat over a satin green
  const m = mask
    ? new THREE.MeshPhysicalMaterial({ color: MASK, roughness: 0.55, metalness: 0, clearcoat: 0.3, clearcoatRoughness: 0.45 })
    : new THREE.MeshStandardMaterial({ color: EDGE, roughness: 0.7, metalness: 0 });
  m.envMapIntensity = 0.9;
  if (mask) {
    m.userData.layerMask = true;
    m.onBeforeCompile = sh => {
      sh.uniforms.uMaskX = layers.uMaskX;
      sh.uniforms.uFr4 = { value: new THREE.Color(FR4) };
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying float vLx;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvLx = position.x;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform float uMaskX; uniform vec3 uFr4; varying float vLx;')
        .replace('#include <color_fragment>', `#include <color_fragment>
          float mk = smoothstep(uMaskX + 0.006, uMaskX - 0.006, vLx);
          diffuseColor.rgb = mix(uFr4, diffuseColor.rgb, mk);`)
        .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
          roughnessFactor = mix(0.75, roughnessFactor, mk);`)
        .replace('#include <lights_physical_fragment>', `#include <lights_physical_fragment>
          material.clearcoat *= mk;   // bare laminate has no lacquer`);
    };
  }
  return m;
}

/* Distance from point q to segment ab. */
const segDist = (q, a, b) => {
  const ab = b.clone().sub(a), t = clamp(q.clone().sub(a).dot(ab) / Math.max(ab.lengthSq(), 1e-9));
  return q.distanceTo(a.clone().addScaledVector(ab, t));
};

/* Flat ribbons along polylines, each vertex carrying its draw order — the
   copper and silkscreen layers are built from these. */
class RibbonBuilder {
  constructor() { this.p = []; this.o = []; this.i = []; }
  quad(a, b, w, o0, o1, y) {
    const d = new THREE.Vector3().subVectors(b, a).setY(0);
    const len = d.length(); if (len < 1e-6) return;
    d.divideScalar(len);
    const n = new THREE.Vector3(-d.z, 0, d.x).multiplyScalar(w / 2);
    // stretch by half a width so the joints overlap instead of notching
    const A = a.clone().addScaledVector(d, -w / 2), B = b.clone().addScaledVector(d, w / 2);
    const base = this.p.length / 3;
    [[A, -1, o0], [A, 1, o0], [B, 1, o1], [B, -1, o1]].forEach(([q, s, o]) => {
      this.p.push(q.x + n.x * s, y, q.z + n.z * s); this.o.push(o);
    });
    this.i.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  poly(pts, w, o0, o1, y, closed = false) {
    const P = closed ? [...pts, pts[0]] : pts;
    let L = 0; for (let k = 1; k < P.length; k++) L += P[k].distanceTo(P[k - 1]);
    let acc = 0;
    for (let k = 1; k < P.length; k++) {
      const d = P[k].distanceTo(P[k - 1]);
      this.quad(P[k - 1], P[k], w, o0 + (o1 - o0) * (acc / L), o0 + (o1 - o0) * ((acc + d) / L), y);
      acc += d;
    }
  }
  disc(c, r, o, y, seg = 12) {
    const base = this.p.length / 3;
    this.p.push(c.x, y, c.z); this.o.push(o);
    for (let k = 0; k < seg; k++) { const a = (k / seg) * Math.PI * 2; this.p.push(c.x + Math.cos(a) * r, y, c.z + Math.sin(a) * r); this.o.push(o); }
    for (let k = 0; k < seg; k++) this.i.push(base, base + 1 + ((k + 1) % seg), base + 1 + k);
  }
  build(material) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.p, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.p.map((_, k) => (k % 3 === 1 ? 1 : 0)), 3));
    g.setAttribute('aOrder', new THREE.Float32BufferAttribute(this.o, 1));
    g.setIndex(this.i);
    const m = new THREE.Mesh(g, material);
    m.receiveShadow = true;
    return m;
  }
}

/* A layer that grows in by draw order (uGrow). Copper also changes once the
   mask has flowed over it: traces take the lighter green of mask-over-copper,
   pads stay open in the mask and get their gold (ENIG) finish. */
function layerMaterial({ color, roughness, metalness, grow, layers, copper }) {
  const gold = copper === 'gold';
  // linear colours: ENIG gold, and the slightly lighter green of mask over raised copper
  const under = gold ? 'vec3(0.62, 0.42, 0.12)' : 'vec3(0.016, 0.14, 0.045)';
  const m = new THREE.MeshStandardMaterial({ color, roughness, metalness, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  m.envMapIntensity = 1;
  m.customProgramCacheKey = () => 'zx-layer-' + (copper || 'plain');   // same hook source, different shaders
  m.onBeforeCompile = sh => {
    sh.uniforms.uGrow = grow;
    sh.uniforms.uMaskX = layers.uMaskX;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aOrder; varying float vOrder; varying float vLx;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvOrder = aOrder; vLx = position.x;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uGrow, uMaskX; varying float vOrder; varying float vLx;')
      .replace('#include <clipping_planes_fragment>', '#include <clipping_planes_fragment>\nif (vOrder > uGrow) discard;')
      .replace('#include <color_fragment>', copper ? `#include <color_fragment>
          float mk = smoothstep(uMaskX + 0.006, uMaskX - 0.006, vLx);
          diffuseColor.rgb = mix(diffuseColor.rgb, ${under}, mk);` : '#include <color_fragment>')
      .replace('#include <metalnessmap_fragment>', copper ? `#include <metalnessmap_fragment>\nmetalnessFactor = mix(metalnessFactor, ${gold ? '1.0' : '0.08'}, mk);` : '#include <metalnessmap_fragment>')
      .replace('#include <roughnessmap_fragment>', copper ? `#include <roughnessmap_fragment>\nroughnessFactor = mix(roughnessFactor, ${gold ? '0.32' : '0.5'}, mk);` : '#include <roughnessmap_fragment>');
  };
  return m;
}

export class Board {
  constructor(gltf) {
    this.root = new THREE.Group();
    this.root.name = 'board';
    const scene = gltf.scene;
    scene.rotation.x = -Math.PI / 2;
    scene.scale.setScalar(UNIT);
    scene.updateMatrixWorld(true);

    /* collect parts */
    const parts = [];
    let boardNode = null;
    scene.children.forEach(n => {
      const box = new THREE.Box3().setFromObject(n);
      if (box.isEmpty()) return;
      const p = { node: n, box, name: n.name };
      if (/^Board/i.test(n.name)) boardNode = p; else parts.push(p);
    });
    const bb = boardNode ? boardNode.box : new THREE.Box3().setFromObject(scene);
    const c = bb.getCenter(new THREE.Vector3());
    const offset = new THREE.Matrix4().makeTranslation(-c.x, -bb.max.y, -c.z);
    this.w = bb.max.x - bb.min.x;
    this.h = bb.max.z - bb.min.z;
    this.thick = bb.max.y - bb.min.y;
    this.mm = { w: (this.w / UNIT) * 1000, h: (this.h / UNIT) * 1000 };

    /* the layer build-up: bare core → copper → solder mask → silkscreen */
    this.layers = { uMaskX: { value: -1e4 }, uCopper: { value: 0 }, uSilk: { value: 0 } };

    /* materials keyed by colour */
    const mats = new Map();
    const matFor = (src, isBoard) => {
      const k = (isBoard ? 'board:' : '') + src.color.getHexString();
      if (!mats.has(k)) mats.set(k, isBoard ? boardMaterial(src.color, this.layers) : gradeMaterial(src.color));
      return mats.get(k);
    };

    const bakeInto = (p, bucket, isBoard) => {
      p.node.traverse(o => {
        if (!o.isMesh) return;
        const m = new THREE.Matrix4().multiplyMatrices(offset, o.matrixWorld);
        const mat = matFor(o.material, isBoard);
        if (!bucket.has(mat)) bucket.set(mat, []);
        bucket.get(mat).push(toFloatGeometry(o.geometry, m));
      });
    };
    const flush = (bucket, group) => {
      for (const [mat, geos] of bucket) {
        const g = geos.length === 1 ? geos[0] : mergeGeometries(geos, false);
        const mesh = new THREE.Mesh(g, mat);
        mesh.castShadow = true; mesh.receiveShadow = true;
        group.add(mesh);
      }
    };

    /* substrate + bottom-side parts: revealed by a sweeping clip plane */
    this.clip = new THREE.Plane(new THREE.Vector3(-1, 0, 0), -this.w);
    this.substrate = new THREE.Group();
    const sub = new Map();
    if (boardNode) bakeInto(boardNode, sub, true);
    const top = [], bottom = [];
    const topY = bb.max.y;
    for (const p of parts) (p.box.min.y >= topY - 0.004 ? top : bottom).push(p);
    bottom.forEach(p => bakeInto(p, sub));
    flush(sub, this.substrate);
    this.substrate.traverse(o => {
      if (o.isMesh) {
        const src = o.material;
        o.material = src.clone();
        o.material.onBeforeCompile = src.onBeforeCompile;
        o.material.clippingPlanes = [this.clip];
        o.material.clipShadows = true;
      }
    });
    this.root.add(this.substrate);

    /* top parts → waves, ordered outward from the biggest package (the FPGA) */
    const vol = p => { const s = p.box.getSize(new THREE.Vector3()); return s.x * s.y * s.z; };
    top.forEach(p => { p.c = p.box.getCenter(new THREE.Vector3()).applyMatrix4(offset); p.v = vol(p); });
    const hero = top.reduce((a, b) => (b.v > a.v ? b : a), top[0]);
    this.heroPos = hero ? hero.c.clone().setY(0) : new THREE.Vector3();
    const big = top.filter(p => p.v > hero.v * 0.04).sort((a, b) => b.v - a.v);
    const small = top.filter(p => !big.includes(p)).sort((a, b) => a.c.distanceTo(this.heroPos) - b.c.distanceTo(this.heroPos));
    const waves = [];
    big.forEach(p => waves.push([p]));
    const PER = Math.max(8, Math.ceil(small.length / 18));
    for (let i = 0; i < small.length; i += PER) waves.push(small.slice(i, i + PER));
    this.waves = waves.map((ps, i) => {
      const g = new THREE.Group();
      const bucket = new Map();
      ps.forEach(p => bakeInto(p, bucket));
      flush(bucket, g);
      g.visible = false;
      this.root.add(g);
      return { group: g, parts: ps, t: i / waves.length };
    });

    /* blueprint drawing */
    this.lineMat = makeLineMaterial();
    this.traces = [];
    this.lines = this.buildBlueprint(top);
    this.root.add(this.lines);
    this.buildLayers(top);

    /* labels the DOM overlay can pin to */
    const hw = this.w / 2, hh = this.h / 2;
    this.anchors = {
      dimW: new THREE.Vector3(0, 0, hh + 0.16),
      dimH: new THREE.Vector3(-hw - 0.16, 0, 0),
      fpga: this.heroPos.clone(),
      cornerA: new THREE.Vector3(hw, 0, -hh),
      cornerB: new THREE.Vector3(-hw, 0, hh),
    };
  }

  buildBlueprint(top) {
    const L = new LineBuilder();
    const hw = this.w / 2, hh = this.h / 2, y = 0.002;
    const V = (x, z) => new THREE.Vector3(x, y, z);

    // board outline — first thing on the page (hero)
    L.poly(roundedRect(-hw, -hh, hw, hh, 0.03, y), 0.0, 0.1, 0, true);
    // dimensions
    const dz = hh + 0.16, dx = -hw - 0.16, t = 0.03;
    L.seg(V(-hw, hh + 0.04), V(-hw, dz + t), 0.1, 0.12); L.seg(V(hw, hh + 0.04), V(hw, dz + t), 0.1, 0.12);
    L.seg(V(-hw, dz), V(hw, dz), 0.11, 0.15);
    L.seg(V(-hw - 0.04, -hh), V(dx - t, -hh), 0.1, 0.12); L.seg(V(-hw - 0.04, hh), V(dx - t, hh), 0.1, 0.12);
    L.seg(V(dx, -hh), V(dx, hh), 0.11, 0.15);
    // arrow ticks
    [[-hw, dz, 1], [hw, dz, -1]].forEach(([x, z, s]) => { L.seg(V(x, z), V(x + s * 0.03, z - 0.015), 0.15, 0.16); L.seg(V(x, z), V(x + s * 0.03, z + 0.015), 0.15, 0.16); });
    [[dx, -hh, 1], [dx, hh, -1]].forEach(([x, z, s]) => { L.seg(V(x, z), V(x - 0.015, z + s * 0.03), 0.15, 0.16); L.seg(V(x, z), V(x + 0.015, z + s * 0.03), 0.15, 0.16); });
    // centre mark
    L.seg(V(-0.05, 0), V(0.05, 0), 0.14, 0.16); L.seg(V(0, -0.05), V(0, 0.05), 0.14, 0.16);

    // footprints, outward from the FPGA — the Idea chapter
    const sorted = [...top].sort((a, b) => a.c.distanceTo(this.heroPos) - b.c.distanceTo(this.heroPos));
    sorted.forEach((p, i) => {
      const o = 0.22 + (i / sorted.length) * 0.4;
      const b = p.box, off = p.c.clone().sub(p.box.getCenter(new THREE.Vector3()));
      const x0 = b.min.x + off.x, x1 = b.max.x + off.x, z0 = b.min.z + off.z, z1 = b.max.z + off.z;
      L.poly([V(x0, z0), V(x1, z0), V(x1, z1), V(x0, z1)], o, o + 0.04, 0, true);
      p.order = o;
      p.rect = [x0, z0, x1, z1];
      // pin-1 dot on the bigger packages
      if (x1 - x0 > 0.03 && z1 - z0 > 0.03) L.seg(V(x0 + 0.008, z0 + 0.008), V(x0 + 0.014, z0 + 0.014), o + 0.04, o + 0.045);
    });

    // traces — from the end of each part that faces a package to a point on that
    // package's facing edge: straight where they line up, else one leg, a 45° knee
    // and a square entry. A route is kept only if it skirts every other footprint
    // and clears the routes already laid, so nothing crosses or bunches up.
    const hubs = sorted.filter(p => p.v > 1e-7).slice(0, 14);
    const inside = q => Math.abs(q.x) < hw - 0.02 && Math.abs(q.z) < hh - 0.02;
    const M = 0.0025;
    // its own two ends may be touched at the edge, never crossed
    const hitsPart = (q, ends) => sorted.some(p => {
      const m = ends.includes(p) ? -0.001 : M;
      return q.x > p.rect[0] - m && q.x < p.rect[2] + m && q.z > p.rect[1] - m && q.z < p.rect[3] + m;
    });
    const sample = pts => {
      const out = [];
      for (let k = 1; k < pts.length; k++) {
        const a = pts[k - 1], d = a.distanceTo(pts[k]);
        for (let u = 0; u <= d; u += 0.003) out.push(a.clone().lerp(pts[k], u / d));
      }
      return out;
    };
    const route = (p, hub) => {
      const [x0, z0, x1, z1] = p.rect, [hx0, hz0, hx1, hz1] = hub.rect;
      const ins = Math.min(0.008, (hx1 - hx0) * 0.3, (hz1 - hz0) * 0.3);
      const c = p.c, dx = hub.c.x - c.x, dz = hub.c.z - c.z;
      const sideX = c.z >= hz0 && c.z <= hz1 ? true : c.x >= hx0 && c.x <= hx1 ? false : Math.abs(dx) > Math.abs(dz);
      const b = sideX ? V(dx > 0 ? hx0 : hx1, clamp(c.z, hz0 + ins, hz1 - ins)) : V(clamp(c.x, hx0 + ins, hx1 - ins), dz > 0 ? hz0 : hz1);
      const pts = [];
      if (sideX) {
        if (Math.abs(b.z - c.z) < 1e-4) pts.push(V(dx > 0 ? x1 : x0, c.z), b);
        else {
          const sz = Math.sign(b.z - c.z), sx = Math.sign(dx), a = V(c.x, sz > 0 ? z1 : z0);
          const k = Math.min(Math.abs(b.x - a.x) * 0.5, Math.abs(b.z - a.z) * 0.5, 0.03);
          pts.push(a, V(a.x, b.z - sz * k), V(a.x + sx * k, b.z), b);
        }
      } else if (Math.abs(b.x - c.x) < 1e-4) pts.push(V(c.x, dz > 0 ? z1 : z0), b);
      else {
        const sx = Math.sign(b.x - c.x), sz = Math.sign(dz), a = V(sx > 0 ? x1 : x0, c.z);
        const k = Math.min(Math.abs(b.x - a.x) * 0.5, Math.abs(b.z - a.z) * 0.5, 0.03);
        pts.push(a, V(b.x - sx * k, a.z), V(b.x, a.z + sz * k), b);
      }
      let len = 0; for (let k = 1; k < pts.length; k++) len += pts[k].distanceTo(pts[k - 1]);
      if (len < 0.01 || len > 0.32 || !pts.every(inside)) return null;
      if (sample(pts).some(q => hitsPart(q, [p, hub]))) return null;
      return { p, pts, len, w: p.v > 1e-7 ? 0.0075 : 0.0055 };
    };
    // every part offers a route to its three nearest packages and its four
    // nearest neighbours (the local nets); the shortest go down first, at most
    // two per part and one per pair
    // parts sat inside another's outline (a die, a lid) carry no nets of their own
    const nested = new Set(sorted.filter(p => sorted.some(o => o !== p && o.rect[0] <= p.c.x && p.c.x <= o.rect[2] && o.rect[1] <= p.c.z && p.c.z <= o.rect[3]
      && (o.rect[2] - o.rect[0]) * (o.rect[3] - o.rect[1]) > (p.rect[2] - p.rect[0]) * (p.rect[3] - p.rect[1]))));
    const near = (p, set, n) => set.filter(h => h !== p && !nested.has(h)).sort((a, b) => a.c.distanceTo(p.c) - b.c.distanceTo(p.c)).slice(0, n);
    const cands = [];
    sorted.forEach(p => {
      if (nested.has(p)) return;
      new Set([...near(p, hubs, 3), ...near(p, sorted, 4)]).forEach(h => { const r = route(p, h); if (r) { r.h = h; cands.push(r); } });
    });
    cands.sort((a, b) => a.len - b.len);
    const laid = [], count = new Map(), pairs = new Set(), kept = [];
    cands.forEach(r => {
      const key = [sorted.indexOf(r.p), sorted.indexOf(r.h)].sort((a, b) => a - b).join();
      if ((count.get(r.p) || 0) >= 2 || pairs.has(key)) return;
      const clear = sample(r.pts).every(q => laid.every(s => segDist(q, s.a, s.b) > (r.w + s.w) / 2 + 0.003));
      if (!clear) return;
      for (let k = 1; k < r.pts.length; k++) laid.push({ a: r.pts[k - 1], b: r.pts[k], w: r.w });
      count.set(r.p, (count.get(r.p) || 0) + 1); pairs.add(key); kept.push(r);
    });
    // drawn outward from the FPGA, like the footprints
    kept.sort((a, b) => sorted.indexOf(a.p) - sorted.indexOf(b.p)).forEach((r, n) => {
      const o = 0.5 + (n / kept.length) * 0.45;
      L.poly(r.pts, o, o + 0.05, 0);
      this.traces.push({ pts: r.pts, w: r.w });
    });
    return L.build(this.lineMat);
  }

  /* The copper and silkscreen layers, drawn from the same footprints and
     routes as the blueprint so the lines on paper become the metal. */
  buildLayers(top) {
    const fp = p => {
      const b = p.box, off = p.c.clone().sub(b.getCenter(new THREE.Vector3()));
      return [b.min.x + off.x, b.min.z + off.z, b.max.x + off.x, b.max.z + off.z];
    };
    const V = (x, z) => new THREE.Vector3(x, 0, z);
    const Y = { trace: 0.0012, via: 0.0018, pad: 0.0024, silk: 0.0032 };   // each film at its own height: nothing coplanar
    const sorted = [...top].sort((a, b) => a.c.distanceTo(this.heroPos) - b.c.distanceTo(this.heroPos));
    const rank = i => i / Math.max(1, sorted.length - 1);

    // pads: two ends for the small passives, a perimeter row for the packages
    const pads = new RibbonBuilder();
    sorted.forEach((p, i) => {
      const [x0, z0, x1, z1] = fp(p), o = rank(i) * 0.35;
      const sx = x1 - x0, sz = z1 - z0;
      if (sx < 0.03 && sz < 0.03) {
        const alongX = sx >= sz, L = (alongX ? sx : sz) * 0.32, W = (alongX ? sz : sx) * 0.9;
        const ends = alongX ? [[V(x0, (z0 + z1) / 2), V(x0 + L, (z0 + z1) / 2)], [V(x1 - L, (z0 + z1) / 2), V(x1, (z0 + z1) / 2)]]
          : [[V((x0 + x1) / 2, z0), V((x0 + x1) / 2, z0 + L)], [V((x0 + x1) / 2, z1 - L), V((x0 + x1) / 2, z1)]];
        ends.forEach(([a, b]) => pads.quad(a, b, W, o, o, Y.pad));
      } else {
        const step = 0.011, len = 0.008, wid = 0.0045, in_ = 0.003;
        for (let x = x0 + step; x < x1 - step / 2; x += step) {
          pads.quad(V(x, z0 + in_), V(x, z0 + in_ + len), wid, o, o, Y.pad);
          pads.quad(V(x, z1 - in_ - len), V(x, z1 - in_), wid, o, o, Y.pad);
        }
        for (let z = z0 + step; z < z1 - step / 2; z += step) {
          pads.quad(V(x0 + in_, z), V(x0 + in_ + len, z), wid, o, o, Y.pad);
          pads.quad(V(x1 - in_ - len, z), V(x1 - in_, z), wid, o, o, Y.pad);
        }
      }
    });

    // traces (already cleared of each other and the parts) + vias at the knees
    const routes = this.traces;
    const cu = new RibbonBuilder();
    routes.forEach((t, i) => {
      const o = 0.2 + (i / routes.length) * 0.65;
      cu.poly(t.pts, t.w, o, Math.min(o + 0.15, 1), Y.trace);
      if (i % 3 === 0 && t.pts.length > 2) cu.disc(t.pts[2], 0.0065, Math.min(o + 0.1, 1), Y.via);
    });

    // silkscreen: outlines just outside each footprint, pin-1 marks on packages
    const silk = new RibbonBuilder();
    sorted.forEach((p, i) => {
      const [x0, z0, x1, z1] = fp(p), o = rank(i) * 0.85, g = 0.004;
      silk.poly([V(x0 - g, z0 - g), V(x1 + g, z0 - g), V(x1 + g, z1 + g), V(x0 - g, z1 + g)], 0.0016, o, Math.min(o + 0.15, 1), Y.silk, true);
      if (x1 - x0 > 0.03 && z1 - z0 > 0.03) silk.disc(V(x0 - g - 0.006, z0 - g - 0.006), 0.0025, Math.min(o + 0.15, 1), Y.silk);
    });

    const copperMat = layerMaterial({ color: 0xc8794a, roughness: 0.34, metalness: 0.9, grow: this.layers.uCopper, layers: this.layers, copper: 'green' });
    const padMat = layerMaterial({ color: 0xc8794a, roughness: 0.34, metalness: 0.9, grow: this.layers.uCopper, layers: this.layers, copper: 'gold' });
    const silkMat = layerMaterial({ color: 0xeeeee6, roughness: 0.85, metalness: 0, grow: this.layers.uSilk, layers: this.layers });
    this.layerGroup = new THREE.Group();
    [pads.build(padMat), cu.build(copperMat), silk.build(silkMat)].forEach(m => this.layerGroup.add(m));
    this.root.add(this.layerGroup);
  }

  /* 0..1 of the blueprint drawn */
  setReveal(r) { this.lineMat.uniforms.uReveal.value = r; }
  setLines(opacity) { this.lineMat.uniforms.uOpacity.value = opacity; this.lines.visible = opacity > 0.001; }

  /* 0..1 of the substrate swept in */
  setSubstrate(p) {
    // plane is in world space; the sweep only ever runs with the board at the origin
    const hw = this.w / 2 + 0.02;
    this.root.updateWorldMatrix(true, false);
    const x0 = this.root.matrixWorld.elements[12];
    this.clip.constant = p <= 0 ? -1e4 : p >= 1 ? 1e4 : x0 - hw + p * hw * 2;
    this.substrate.visible = p > 0;
  }

  /* 0..1 each: copper etched, mask flowed across (−x → +x), silkscreen printed */
  setLayers(copper, mask, silk) {
    const hw = this.w / 2 + 0.03;
    this.layers.uCopper.value = copper;
    this.layers.uMaskX.value = mask <= 0 ? -1e4 : mask >= 1 ? 1e4 : -hw + mask * hw * 2;
    this.layers.uSilk.value = silk;
    this.layerGroup.visible = copper > 0 && this.substrate.visible;
    this.layerGroup.children[2].visible = silk > 0;
  }

  /* 0..1 of the parts placed */
  setPlacement(p) {
    const n = this.waves.length;
    this.waves.forEach((w, i) => {
      const t0 = (i / n) * 0.85;
      const k = clamp(invLerp(t0, t0 + 0.15, p));
      w.group.visible = k > 0;
      w.group.position.y = (1 - easeOut(k)) * 0.45;
    });
  }

  setShadows(on) {
    this.root.traverse(o => { if (o.isMesh) o.castShadow = on; });
    this.layerGroup.traverse(o => { o.castShadow = false; });   // films on the face: nothing to cast
  }
}
